import type {
  DashboardMetric,
  FaqItem,
  FeaturedWorkItem,
  RoadmapStep,
  ServiceItem,
  StatItem,
  TestimonialItem,
} from "@/types";

export const LOGOS: string[] = [
  "NorthVault",
  "Helix Health",
  "Lumen Commerce",
  "Orbit AI",
  "Stratos",
  "Aurora Labs",
  "Polaris",
  "Vanta",
  "Glimmer",
  "TechCorp",
];

export const LOGO_STRIP_CAPTION = "Trusted by ambitious teams worldwide";

export const HERO_STATUS_ITEMS: string[] = [
  "24 active engagements",
  "Avg. response time < 4h",
  "First production PR within 5 business days",
];

export const STATS: StatItem[] = [
  { label: "Projects Delivered", value: 500, suffix: "+" },
  { label: "Senior Engineers", value: 80, suffix: "+" },
  { label: "Client Retention", value: 99, suffix: "%" },
  { label: "Industries Served", value: 15, suffix: "+" },
];

export const SERVICES: ServiceItem[] = [
  {
    title: "AI Enablement",
    description: "Strategy, RAG/Search layers, and seamless model integration into your stack.",
    stack: "OpenAI · Anthropic · Python",
    image: "/images/ai-enablement.jpg",
    slug: "ai-enablement",
  },
  {
    title: "AI Agents",
    description: "Autonomous workflows, tool-calling agents and multi-agent orchestration.",
    stack: "LangGraph · MCP · TypeScript",
    image: "/images/ai-agent.jpg",
    slug: "ai-agents",
  },
  {
    title: "Web & Mobile",
    description: "Next.js, React Native and Flutter with unified design systems.",
    stack: "Next.js · React · Node.js",
    image: "/images/web-and-mobile.webp",
    slug: "web-mobile",
  },
  {
    title: "Staff Augmentation",
    description: "Senior-level talent (8+ yrs avg) with fast onboarding and zero ramp-up.",
    stack: "GitHub · Jira · CI/CD",
    image: "/images/staff-augmentation.jpg",
    slug: "staff-augmentation",
  },
  {
    title: "Blockchain",
    description: "Smart contracts, Web3 platforms and DeFi-ready architecture.",
    stack: "Solidity · Foundry · EVM",
    image: "/images/blockchain.jpg",
    slug: "blockchain",
  },
  {
    title: "Custom Platforms",
    description: "Bespoke SaaS, internal tools, and admin systems engineered to fit.",
    stack: "tRPC · PostgreSQL · Redis",
    image: "/images/custom-platforms.jpg",
    slug: "custom-platforms",
  },
];

export const FEATURED_WORK: FeaturedWorkItem[] = [
  {
    title: "Sales Ops Agent Platform",
    meta: "LangGraph · Salesforce",
    blurb: "Autonomous lead qualification and pipeline hygiene across distributed GTM operations.",
    image: "/images/sales-ops-agent-platform.jpg",
  },
  {
    title: "Healthcare AI Skin Analyst",
    meta: "FastAPI · HIPAA",
    blurb: "Clinical image triage with secure pipelines and compliant physician handoff workflows.",
    image: "/images/healthcare-ai-skin-analyst.jpg",
  },
  {
    title: "DeFi Yield Vault",
    meta: "$80M TVL",
    blurb: "On-chain automated strategy vault with advanced risk controls and treasury observability.",
    image: "/images/defi-yield-vault.jpg",
  },
  {
    title: "E-commerce Admin",
    meta: "$4M/mo GMV",
    blurb: "Unified command center for merchandising, orders, fulfillment, and growth analytics.",
    image: "/images/e-commerce-admin.jpg",
  },
];

export const INSIDE_CYBITIX_FEATURES: string[] = [
  "8+ years average engineering experience",
  "First production PR within 5 business days",
  "Security & observability engineered from day one",
];

export const ROADMAP_STEPS: RoadmapStep[] = [
  { step: "Strategy", description: "Discovery, technical audit & measurable goals." },
  { step: "Design", description: "UX-first, pixel-perfect design systems." },
  { step: "Development & Testing", description: "Iterative sprints with QA built in." },
  { step: "Launch", description: "Production-grade deployments with monitoring." },
  { step: "Support", description: "24/7 SLAs, optimization & continuous delivery." },
];

export const DASHBOARD_METRICS: DashboardMetric[] = [
  { label: "Revenue", value: "$248,392" },
  { label: "Orders", value: "1,284" },
  { label: "Uptime", value: "99.98%" },
];

export const DASHBOARD_FEATURES: string[] = [
  "Live sprint velocity & burndown",
  "Role-based access for stakeholders",
  "Order, inventory & customer modules",
  "Real-time analytics & exportable reports",
];

export const SPRINT_BARS: number[] = [55, 72, 60, 80, 76, 90];

export const TESTIMONIALS: TestimonialItem[] = [
  {
    quote: "The admin dashboard they built powers $4M/mo in orders. Zero downtime in 14 months. Phenomenal.",
    author: "Marco Bianchi",
    role: "Founder, Lumen Commerce",
    initials: "MB",
    photo: "/images/marco-bianchi.jpg",
  },
  {
    quote: "Their AI skin-analysis pipeline was production-grade on day one. Measurable accuracy gains within the first sprint.",
    author: "Aisha Rahman",
    role: "VP Engineering, Helix Health",
    initials: "AR",
    photo: "/images/aisha-rahman.jpg",
  },
  {
    quote: "Cybitix shipped a production-safe agent platform in six weeks. Senior execution with zero churn.",
    author: "Sarah Mitchell",
    role: "CTO, TechCorp",
    initials: "SM",
    photo: "/images/sarah-mitchell.jpg",
  },
];

export const FAQS: FaqItem[] = [
  {
    q: "How quickly can Cybitix start on my project?",
    a: "For most engagements we can have a senior engineer onboarded and shipping PRs within 5 business days. Discovery and SOW typically take 2–3 days.",
  },
  {
    q: "Do you only work with enterprises, or also startups?",
    a: "We work with both. Most clients are growth-stage startups or enterprise teams that need senior execution without the overhead of a large agency.",
  },
  {
    q: "What is your pricing model?",
    a: "We offer time-and-materials retainers, fixed-scope project pricing, and dedicated team models. We'll recommend the best fit after a discovery call.",
  },
  {
    q: "Which AI models and stacks do you specialize in?",
    a: "We work with OpenAI, Anthropic, and open-source models. Our core stack includes Python, LangGraph, and TypeScript for agent systems.",
  },
  {
    q: "Do you sign NDAs and handle IP transfer?",
    a: "Yes. We sign mutual NDAs before any discovery call and full IP transfer is standard in all our contracts.",
  },
  {
    q: "How do you ensure code quality and security?",
    a: "We apply SOC2-aligned controls, least-privilege access, encrypted secrets, release audit logging, and mandatory peer review on every PR.",
  },
];
